'use client'

import React, { useCallback } from 'react'
import { useDismissablePopover } from '@kiao-voice/hooks'
import { Building2, Check, ChevronDown, LogOut, Mail, User } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuthModal } from '@/features/auth/auth-modal-provider'

/** One workspace row in the switcher (already localized by the caller). */
export interface AccountContextView {
  /** `'personal'` or `org:<id>` — the `X-Active-Context` key. */
  key: string
  /** Row title — the workspace name (個人 / org name). */
  name: string
  /** Row sub-line — the plan / seat summary. */
  sub: string
  /** Personal context → person glyph; team → building glyph. */
  isPersonal: boolean
  /** The currently-acting context — gets the check + surface highlight. */
  isActive: boolean
}

interface AccountMenuProps {
  /** Signed-in email, shown in the trigger row. */
  email: string | null
  /** Sub-line under the email (個人 · {plan} / {org} · 團隊席次), or null. */
  identityLine: string | null
  /**
   * Workspace switcher rows. Rendered only when there is a real choice
   * (length > 1). A solo user passes `[]` and gets the team upsell instead.
   */
  contexts: AccountContextView[]
  /** Fired when the user picks a workspace. */
  onSelectContext: (key: string) => void
  /**
   * Solo (no team at all): show the 「了解團隊方案 ↗」 entry → /pricing#team in a
   * new tab, instead of a switcher the user has nothing to switch within.
   */
  showTeamUpsell: boolean
  /** Fired when the user clicks 登出. */
  onSignOut: () => void
}

/**
 * The bottom-left account identity row + its popup, shared by every logged-in
 * page through {@link AppShell}. Ports the `data-od-id="sidebar"` side-foot from
 * the design prototypes (app-account / app-team / app-transcribe) and the
 * open/close + outside-click + Escape behavior from design/js/kiao-account.js.
 *
 * Presentational: the role/context derivation lives in `AppShell`; this owns
 * only the popup's open state. All chrome styles come from ui-kit `kiao-app.css`.
 */
export function AccountMenu({
  email,
  identityLine,
  contexts,
  onSelectContext,
  showTeamUpsell,
  onSignOut,
}: AccountMenuProps) {
  const { t } = useTranslation(['common', 'web'])
  const { open: openSignIn } = useAuthModal()
  // Popup open/close + outside-click (mousedown) + Escape (restore focus) come
  // from the shared hook — was a duplicated effect; matches design/js/kiao-account.js.
  const {
    open: menuOpen,
    toggle: toggleMenu,
    close: closeMenu,
    containerRef: footRef,
    triggerRef,
  } = useDismissablePopover<HTMLDivElement, HTMLButtonElement>()

  const handleSelect = useCallback(
    (key: string) => {
      closeMenu()
      onSelectContext(key)
    },
    [closeMenu, onSelectContext]
  )

  const handleSignOut = useCallback(() => {
    closeMenu()
    onSignOut()
  }, [closeMenu, onSignOut])

  const hasSwitcher = contexts.length > 0
  const avatarInitial = email ? email.charAt(0).toUpperCase() : '?'

  // Signed-out state: the (app) shell can mount on a self-guarded surface (e.g.
  // /transcriptions, which shows an in-content sign-in CTA without redirecting)
  // before the user has a token. Offer an in-place sign-in here rather than a
  // broken account row whose only action would be a sign-out-while-signed-out.
  if (email === null) {
    return (
      <div className="side-foot">
        <button
          className="side-link"
          type="button"
          onClick={() => openSignIn({ redirectTo: null })}
        >
          {t('web:common.signIn')}
        </button>
      </div>
    )
  }

  return (
    <div className="side-foot" ref={footRef}>
      <button
        ref={triggerRef}
        className="acct-row"
        type="button"
        aria-haspopup="true"
        aria-expanded={menuOpen}
        aria-label={t('web:appShell.acctMenuLabel')}
        onClick={toggleMenu}
      >
        <span className="avatar" aria-hidden="true">
          {avatarInitial}
        </span>
        <span className="acct-id">
          {email !== null && (
            <span className="acct-email" title={email}>
              {email}
            </span>
          )}
          {identityLine && <span className="acct-org">{identityLine}</span>}
        </span>
        <ChevronDown className="acct-chev" aria-hidden="true" />
      </button>

      {menuOpen && (
        <div className="acct-menu" role="menu">
          {hasSwitcher && (
            <>
              <p className="acct-ctx-label" role="presentation">
                {t('web:account.switchWorkspace')}
              </p>
              <div role="group" aria-label={t('web:account.switchWorkspace')}>
                {contexts.map((ctx) => (
                  <button
                    key={ctx.key}
                    type="button"
                    role="menuitemradio"
                    aria-checked={ctx.isActive}
                    className="acct-ctx"
                    data-testid={`ctx-option-${ctx.key}`}
                    onClick={() => handleSelect(ctx.key)}
                  >
                    <span className="ctx-ico" aria-hidden="true">
                      {ctx.isPersonal ? <User /> : <Building2 />}
                    </span>
                    <span className="ctx-meta">
                      <span className="ctx-name">{ctx.name}</span>
                      <span className="ctx-sub">{ctx.sub}</span>
                    </span>
                    <Check className="ctx-check" aria-hidden="true" />
                  </button>
                ))}
              </div>
              <div className="acct-sep" />
            </>
          )}

          {showTeamUpsell && (
            <>
              <div className="acct-team-cta">
                <p className="acct-ctx-label" role="presentation">
                  {t('web:appShell.groupTeam')}
                </p>
                <a
                  className="acct-mi"
                  href="/pricing#team"
                  target="_blank"
                  rel="noreferrer"
                  role="menuitem"
                >
                  <Building2 aria-hidden="true" />
                  <span>{t('web:appShell.learnTeamPlan')}</span>
                  <span className="meta" aria-hidden="true" style={{ marginLeft: 'auto' }}>
                    ↗
                  </span>
                </a>
              </div>
              <div className="acct-sep" />
            </>
          )}

          {/* /contact, not a bare mailto — a mailto only works if the
              visitor's OS has a mail handler configured. See the contact page. */}
          <a className="acct-mi" href="/contact" role="menuitem">
            <Mail aria-hidden="true" />
            <span>{t('web:appShell.contact')}</span>
          </a>
          <div className="acct-sep" />
          <button
            className="acct-mi danger"
            type="button"
            role="menuitem"
            onClick={handleSignOut}
          >
            <LogOut aria-hidden="true" />
            <span>{t('web:common.signOut')}</span>
          </button>
        </div>
      )}
    </div>
  )
}
